import type { DragEvent } from "react";
import type {
  MailPlaceholderDefinition,
  MailPlaceholderValueType,
} from "api/mailTemplates/MailTemplate.typed";
import {
  mailPlaceholderKeyAllowedForValueTypes,
  mailPlaceholderKeyInDefinitionList,
} from "./mailPlaceholderContext";
import { mailPhToken, parseMailPlaceholderKeyFromToken } from "./mailLineParts";

export const MAIL_PLACEHOLDER_DRAG_MIME = "application/x-zekay-mail-placeholder-key";

export function setMailPlaceholderDragData(e: DragEvent, key: string): void {
  e.dataTransfer.setData("text/plain", mailPhToken(key));
  e.dataTransfer.setData(MAIL_PLACEHOLDER_DRAG_MIME, key);
  e.dataTransfer.effectAllowed = "copy";
}

export function mailPlaceholderDragOverHandler(e: DragEvent): void {
  e.preventDefault();
  e.dataTransfer.dropEffect = "copy";
}

export function mailPlaceholderKeyFromDataTransfer(dt: DataTransfer): string | null {
  const fromMime = dt.getData(MAIL_PLACEHOLDER_DRAG_MIME).trim();

  if (fromMime) {
    return fromMime;
  }

  return parseMailPlaceholderKeyFromToken(dt.getData("text/plain").trim());
}

export function mailPlaceholderDropInTextField(
  e: DragEvent<HTMLTextAreaElement | HTMLInputElement>,
  currentValue: string,
  commit: (next: string) => void,
): boolean {
  e.preventDefault();

  const key = mailPlaceholderKeyFromDataTransfer(e.dataTransfer);

  if (!key) {
    return false;
  }

  const token = mailPhToken(key);
  const el = e.currentTarget;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const next = currentValue.slice(0, start) + token + currentValue.slice(end);

  commit(next);

  requestAnimationFrame(() => {
    el.focus();
    const pos = start + token.length;
    el.setSelectionRange(pos, pos);
  });

  return true;
}

export type TextareaPlaceholderDropOptions = {
  catalog: readonly MailPlaceholderDefinition[];
  allowedDefinitions?: readonly MailPlaceholderDefinition[];
  allowedValueTypes?: ReadonlySet<MailPlaceholderValueType>;
  allowUnknownPlaceholderKey?: boolean;
};

export function textareaOrInputPlaceholderDropProps(
  value: string,
  commit: (next: string) => void,
  dropFilter?: TextareaPlaceholderDropOptions,
): {
  onDragOver: (e: DragEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onDrop: (e: DragEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
} {
  return {
    onDragOver: mailPlaceholderDragOverHandler,
    onDrop: (e: DragEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (dropFilter) {
        const key = mailPlaceholderKeyFromDataTransfer(e.dataTransfer);

        if (key) {
          if (dropFilter.allowedDefinitions !== undefined) {
            if (!mailPlaceholderKeyInDefinitionList(key, dropFilter.allowedDefinitions)) {
              e.preventDefault();

              return;
            }
          } else if (dropFilter.allowedValueTypes) {
            if (
              !mailPlaceholderKeyAllowedForValueTypes(
                dropFilter.catalog,
                key,
                dropFilter.allowedValueTypes,
                { allowUnknownKey: dropFilter.allowUnknownPlaceholderKey },
              )
            ) {
              e.preventDefault();

              return;
            }
          }
        }
      }

      mailPlaceholderDropInTextField(e, value, commit);
    },
  };
}
